import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AppHeader, BottomNav } from "@/components/app-header";
import { MapPin, Clock, Navigation, Users, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { driverApi } from "@/lib/api";

export default function DriverDirection() {
  const [formData, setFormData] = useState({
    start_location: "",
    end_location: "",
    departure_time: "",
    seats: 4,
    genderPreference: "any",
    quietRide: false,
    ac: true,
    music: true,
  });
  const [loading, setLoading] = useState(false);
  const [routes, setRoutes] = useState<any[]>([]);

  useEffect(() => {
    fetchRoutes();
  }, []);

  const fetchRoutes = async () => {
    try {
      const response: any = await driverApi.getMyRoutes();
      if (response.success) {
        setRoutes(response.routes);
      }
    } catch (error) {
      console.error("Failed to fetch routes", error);
    }
  };

  const handleDeleteRoute = async (id: string) => {
    try {
      await driverApi.deleteRoute(id);
      setRoutes(routes.filter(r => r.id !== id));
      toast.success("Route deleted successfully");
    } catch (error: any) {
      toast.error(error.message || "Failed to delete route");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await driverApi.setDirection({
        startLocation: formData.start_location,
        endLocation: formData.end_location,
        departureTime: formData.departure_time,
        seats: formData.seats,
        preferences: {
          genderPreference: formData.genderPreference,
          quietRide: formData.quietRide,
          ac: formData.ac,
          music: formData.music
        }
      });

      toast.success("Route posted successfully!");
      fetchRoutes(); // refresh list instead of navigating away
    } catch (error: any) {
      toast.error(error.message || "Failed to post route");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pb-20 md:pb-0">
      <AppHeader title="Post Your Route" />

      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Banner section */}
        <div className="bg-gradient-to-r from-yenko-blue to-blue-600 rounded-3xl p-6 mb-8 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="relative flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
              <Navigation className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-xl font-bold">Share Your Route</h1>
              <p className="text-white/80 text-sm">
                Post where you're going and earn money
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Route Details Card */}
          <div className="bg-white rounded-3xl shadow-[0_2px_10px_rgba(0,0,0,0.06)] border border-gray-100 overflow-hidden">
            <div className="p-5 border-b border-gray-100 bg-gray-50/50">
              <h2 className="font-bold text-gray-900 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-yenko-blue" />
                Route Details
              </h2>
            </div>

            <div className="p-5 space-y-5">
              {/* Start Location */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Starting Point
                </label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 w-3 h-3 bg-green-500 rounded-full" />
                  <Input
                    type="text"
                    placeholder="e.g., Osu, Accra"
                    value={formData.start_location}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        start_location: e.target.value,
                      })
                    }
                    required
                    className="h-14 pl-10 rounded-2xl bg-gray-50 border-0 text-base font-medium placeholder:text-gray-400 focus:ring-2 focus:ring-yenko-blue/20"
                  />
                </div>
              </div>

              {/* Connector */}
              <div className="flex items-center gap-3 px-4">
                <div className="w-0.5 h-8 bg-gray-200 ml-1" />
              </div>

              {/* Destination */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Destination
                </label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 w-3 h-3 bg-yenko-yellow rounded-full" />
                  <Input
                    type="text"
                    placeholder="e.g., Tema, Accra"
                    value={formData.end_location}
                    onChange={(e) =>
                      setFormData({ ...formData, end_location: e.target.value })
                    }
                    required
                    className="h-14 pl-10 rounded-2xl bg-gray-50 border-0 text-base font-medium placeholder:text-gray-400 focus:ring-2 focus:ring-yenko-blue/20"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Time & Seats Card */}
          <div className="bg-white rounded-3xl shadow-[0_2px_10px_rgba(0,0,0,0.06)] border border-gray-100 overflow-hidden">
            <div className="p-5 border-b border-gray-100 bg-gray-50/50">
              <h2 className="font-bold text-gray-900 flex items-center gap-2">
                <Clock className="w-5 h-5 text-yenko-blue" />
                Schedule & Seats
              </h2>
            </div>

            <div className="p-5 space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Departure Time
                </label>
                <Input
                  type="datetime-local"
                  value={formData.departure_time}
                  onChange={(e) =>
                    setFormData({ ...formData, departure_time: e.target.value })
                  }
                  required
                  className="h-14 rounded-2xl bg-gray-50 border-0 text-base font-medium focus:ring-2 focus:ring-yenko-blue/20"
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Available Seats
                </label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setFormData({ ...formData, seats: n });
                      }}
                      className={`flex-1 h-14 rounded-2xl font-bold text-lg transition-all ${
                        formData.seats === n
                          ? "bg-yenko-blue text-white shadow-lg shadow-blue-500/25"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Preferences Card */}
          <div className="bg-white rounded-3xl shadow-[0_2px_10px_rgba(0,0,0,0.06)] border border-gray-100 overflow-hidden">
            <div className="p-5 border-b border-gray-100 bg-gray-50/50">
              <h2 className="font-bold text-gray-900 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-yenko-blue" />
                Ride Preferences
              </h2>
            </div>
            
            <div className="p-5 space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Gender Preference</label>
                <select
                  value={formData.genderPreference}
                  onChange={(e) => setFormData({ ...formData, genderPreference: e.target.value })}
                  className="w-full h-12 rounded-xl bg-gray-50 border border-gray-200 px-4 focus:ring-2 focus:ring-yenko-blue/20"
                >
                  <option value="any">Any Gender</option>
                  <option value="same_gender">Same Gender Only</option>
                  <option value="female_only">Female Only</option>
                  <option value="male_only">Male Only</option>
                </select>
              </div>

              <div className="space-y-4 pt-2 border-t border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">Quiet Ride</h3>
                    <p className="text-sm text-gray-500">I prefer limited conversation</p>
                  </div>
                  <input 
                    type="checkbox" 
                    checked={formData.quietRide}
                    onChange={(e) => setFormData({ ...formData, quietRide: e.target.checked })}
                    className="w-5 h-5 accent-yenko-blue rounded"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">Air Conditioning</h3>
                    <p className="text-sm text-gray-500">AC will be turned on</p>
                  </div>
                  <input 
                    type="checkbox" 
                    checked={formData.ac}
                    onChange={(e) => setFormData({ ...formData, ac: e.target.checked })}
                    className="w-5 h-5 accent-yenko-blue rounded"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">Music</h3>
                    <p className="text-sm text-gray-500">Music will be playing</p>
                  </div>
                  <input 
                    type="checkbox" 
                    checked={formData.music}
                    onChange={(e) => setFormData({ ...formData, music: e.target.checked })}
                    className="w-5 h-5 accent-yenko-blue rounded"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="pt-2">
            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-14 bg-gradient-to-r from-yenko-blue to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-2xl font-bold text-base transition-all"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Posting...
                </div>
              ) : (
                "Post Route"
              )}
            </Button>
          </div>
        </form>

        {/* Active Routes List */}
        {routes.length > 0 && (
          <div className="mt-10 space-y-4">
            <h2 className="font-bold text-gray-900 border-b border-gray-100 pb-2">Your Active Routes</h2>
            <div className="space-y-4">
              {routes.map((route: any) => (
                <div key={route.id} className="bg-white rounded-3xl p-5 shadow-[0_2px_10px_rgba(0,0,0,0.06)] border border-gray-100 flex flex-col gap-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1 pr-4">
                      <p className="font-bold text-gray-900 text-lg leading-snug">
                        {route.start_location?.address || 'Unknown'}
                      </p>
                      <p className="font-bold text-gray-900 text-lg leading-snug mt-1 flex items-center gap-2">
                        <span className="text-gray-400 text-xs uppercase tracking-wider">To</span> {route.end_location?.address || 'Unknown'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 rounded-2xl p-3 flex justify-between items-center border border-gray-100">
                    <div className="flex items-center gap-3">
                      <div className="bg-white text-gray-700 px-3 py-1.5 rounded-lg text-sm font-semibold flex items-center gap-1.5 border border-gray-200">
                        <Clock className="w-4 h-4 text-yenko-blue" />
                        {new Date(route.departure_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                      <div className="bg-white text-gray-700 px-3 py-1.5 rounded-lg text-sm font-semibold flex items-center gap-1.5 border border-gray-200">
                        <Users className="w-4 h-4 text-yenko-blue" />
                        {route.seats} left
                      </div>
                    </div>
                    <Button 
                      variant="ghost" 
                      className="text-red-500 hover:text-red-600 hover:bg-red-50 rounded-xl px-4 py-2 text-sm font-bold transition-colors"
                      onClick={() => handleDeleteRoute(route.id)}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
